import { 
    Container, 
    Typography, 
    Grid, 
    Button, 
    Box,
    Divider,
    Paper
  } from '@mui/material';
  import Hero from '../components/common/Hero';
  import InfoCard from '../components/common/InfoCard';
  import { Link as RouterLink } from 'react-router-dom';
  import { Helmet } from 'react-helmet-async';
  import FacebookIcon from '@mui/icons-material/Facebook';
  import ArticleIcon from '@mui/icons-material/Article';
  import GroupsIcon from '@mui/icons-material/Groups';
  import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
  import PaymentIcon from '@mui/icons-material/Payment';
  import { motion } from 'framer-motion';
  import HomeWorkIcon from '@mui/icons-material/HomeWork';
  
  const Home = () => {
    return (
      <>
        <Helmet>
          <title>Brynleigh Estates | Premier Residential Community in Chelsea, Alabama</title>
          <meta name="description" content="Brynleigh Estates is a premier residential community in Chelsea, Alabama offering beautiful homes, well-maintained grounds, and a strong sense of community." />
          <link rel="canonical" href="https://brynleighestates.com/" />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://brynleighestates.com/" />
          <meta property="og:title" content="Brynleigh Estates | Premier Residential Community in Chelsea, Alabama" />
          <meta property="og:description" content="Brynleigh Estates is a premier residential community in Chelsea, Alabama offering beautiful homes, well-maintained grounds, and a strong sense of community." />
          <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
          <meta property="og:site_name" content="Brynleigh Estates HOA" />
          
          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Brynleigh Estates | Premier Residential Community in Chelsea, Alabama" />
          <meta name="twitter:description" content="Brynleigh Estates is a premier residential community in Chelsea, Alabama offering beautiful homes, well-maintained grounds, and a strong sense of community." />
          <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        </Helmet>
        
        <Hero 
          imageUrl="/images/woods-bg.jpeg"
          title="Brynleigh Estates"
          subtitle="A premier residential community in Chelsea, Alabama"
        />
        
        <Container maxWidth="lg">
          <Box sx={{ my: 6 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <Typography variant="h4" gutterBottom fontWeight="medium">
                  Welcome to Our Community
                </Typography>
                
                <Typography variant="body1" paragraph>
                  There are many things about Brynleigh Estates which make it an ideal place to live...thoughtful neighbors, good schools, cared for pets, manicured lawns, well-behaved children, and much more. We are fortunate to live amongst others who respect local laws as well as the laws of nature.
                </Typography>
                
                <Typography variant="body1" paragraph>
                  We all want the same things, that's why we moved to Brynleigh Estates. When we picked out our lots, we looked around the neighborhood to see how others were living. It was evident, and still is, that the quality of living in Brynleigh Estates is well above average.
                </Typography>
                
                <Box mt={4} display="flex" gap={2} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    component={RouterLink}
                    to="/pay-hoa"
                    startIcon={<PaymentIcon />}
                  >
                    Pay HOA Dues
                  </Button>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ArticleIcon />}
                    component={RouterLink}
                    to="/covenants"
                  >
                    View Covenants
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<FacebookIcon />}
                    href="http://www.facebook.com/groups/BrynleighEstatesResidents"
                    target="_blank"
                  >
                    Join Facebook Group
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h5" gutterBottom fontWeight="medium">
                    Quick Links
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Box component="li" sx={{ mb: 1 }}>
                      <RouterLink to="/board-members">Board and Committee Members</RouterLink>
                    </Box>
                    <Box component="li" sx={{ mb: 1 }}>
                      <RouterLink to="/covenants">Covenant Information</RouterLink>
                    </Box>
                    <Box component="li" sx={{ mb: 1 }}>
                      <RouterLink to="/arc-request">ARC Request Form</RouterLink>
                    </Box>
                    <Box component="li" sx={{ mb: 1 }}>
                      <RouterLink to="/real-estate-info">Real Estate Information</RouterLink>
                    </Box>
                    <Box component="li" sx={{ mb: 1 }}>
                      <RouterLink to="/announcements">Recent Announcements</RouterLink>
                    </Box>
                    <Box component="li" sx={{ mb: 1 }}>
                      <RouterLink to="/update-contact">Update Contact Information</RouterLink>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
  
          <Box sx={{ my: 6 }}>
            <Typography variant="h4" gutterBottom align="center" fontWeight="medium">
              Community Resources
            </Typography>
            <Divider sx={{ mb: 4 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard 
                  icon={<GroupsIcon fontSize="large" color="primary" />}
                  title="About Us" 
                  description="Learn about our community history and values"
                  linkText="Read More"
                  linkTo="/about"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard 
                  icon={<PaymentIcon fontSize="large" color="primary" />}
                  title="Pay HOA Dues" 
                  description="Conveniently pay your annual HOA dues online"
                  linkText="Pay Now"
                  linkTo="/pay-hoa"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard 
                  icon={<ArticleIcon fontSize="large" color="primary" />}
                  title="Documents" 
                  description="Access important HOA documents and forms"
                  linkText="View Documents"
                  linkTo="/covenants"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard 
                  icon={<PhotoLibraryIcon fontSize="large" color="primary" />}
                  title="Photo Gallery" 
                  description="Browse photos from community events"
                  linkText="See Photos"
                  linkTo="/photos"
                />
              </Grid>
            </Grid>
            
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard 
                  icon={<HomeWorkIcon fontSize="large" color="primary" />}
                  title="ARC Request" 
                  description="Submit home modification plans for review"
                  linkText="Submit Request"
                  linkTo="/arc-request"
                />
              </Grid>
            </Grid>
          </Box>
          
          {/* Testimonials Section */}
          <Box sx={{ my: 8, py: 6, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Container maxWidth="lg">
              <Typography variant="h4" gutterBottom align="center" fontWeight="medium">
                What Our Residents Say
              </Typography>
              <Divider sx={{ mb: 4, width: '80px', mx: 'auto' }} />
              
              <Grid container spacing={4} justifyContent="center">
                <Grid item xs={12} md={6} lg={5}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                    component={motion.div}
                    whileHover={{ y: -5, boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }}
                    transition={{ duration: 0.3 }}
                  >
                    <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', flex: 1 }}>
                      "Moving to Brynleigh Estates was the best decision for our family. The community is welcoming, the amenities are excellent, and we love the peaceful environment."
                    </Typography>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        The Johnson Family
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Residents since 2018
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6} lg={5}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                    component={motion.div}
                    whileHover={{ y: -5, boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }}
                    transition={{ duration: 0.3 }}
                  >
                    <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', flex: 1 }}>
                      "The sense of community here is incredible. From neighborhood events to the well-maintained common areas, Brynleigh Estates truly feels like home."
                    </Typography>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Sarah & Michael Davis
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Residents since 2020
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Container>
          </Box>
          
          {/* Call to Action Section */}
          <Box 
            sx={{ 
              my: 8, 
              py: 8, 
              bgcolor: 'primary.main', 
              color: 'white',
              borderRadius: 2,
              backgroundImage: 'linear-gradient(rgba(46, 125, 50, 0.9), rgba(46, 125, 50, 0.9)), url("/images/woods-bg.jpeg")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            component={motion.div}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Container maxWidth="md" sx={{ textAlign: 'center' }}>
              <Typography variant="h3" gutterBottom fontWeight="medium">
                Join Our Community
              </Typography>
              <Typography variant="h6" paragraph sx={{ mb: 4, maxWidth: '700px', mx: 'auto' }}>
                Interested in making Brynleigh Estates your home? Discover available properties and learn more about our community.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  color="secondary"
                  size="large"
                  component={RouterLink}
                  to="/real-estate-info"
                  sx={{ 
                    px: 4, 
                    py: 1.5,
                    fontSize: '1.1rem',
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                    }
                  }}
                >
                  View Available Properties
                </Button>
                <Button 
                  variant="outlined" 
                  color="inherit"
                  size="large"
                  component={RouterLink}
                  to="/contacts"
                  sx={{ 
                    px: 4, 
                    py: 1.5,
                    fontSize: '1.1rem',
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  Contact Us
                </Button>
              </Box>
            </Container>
          </Box>
        </Container>
      </>
    );
  };
  
  export default Home;